import React, { useState, useEffect, useContext } from 'react';
import { Stack } from 'react-bootstrap';
import { UserContext } from '../../../context/user_context/UserContext';
import { Loading } from '../../../components/loading/Loading';
import { useOrganizationContext } from '../../../context/organization_context/OrganizationContext';
import { Card, CardContent, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Link, useMediaQuery, Chip, Button } from '@mui/material';
import { employeeDocumentsSnapshotListener, getEmployeeDocumentDownloadURL } from '../../../repository/DocumentsDAO';
import { formatFirestoreTimestampMMDDYYYY } from '../../../utils/time_utils/TimeUtils';
import { useTranslation } from 'react-i18next';
import { StringKeys } from '../../../resources/strings/StringKeys';
import { employeePerformanceReviewsSnapshotListener } from '../../../repository/EmployeeDAO';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import { useLoadingContext } from '../../../context/loading/LoadingContext';
import { getFunctions, httpsCallable } from 'firebase/functions';

function Profile() {

    const { firebaseUser } = useContext(UserContext);
    const { role, getDepartmentByID } = useOrganizationContext();
    const [documents, setDocuments] = useState([]);
    const { t } = useTranslation();
    const isMobile = useMediaQuery('(max-width:600px)');
    const [performanceReviews, setPerformanceReviews] = useState();
    const navigate = useNavigate()
    const [documentUrls, setDocumentUrls] = useState({});
    const { setLoadingData } = useLoadingContext();
  
    useEffect(() => {
        const loadAllData = async () => {
            if (!firebaseUser) return;
            
            setLoadingData(true);
            try {
                // Set up document listener
                const unsubscribeDocuments = employeeDocumentsSnapshotListener(
                    firebaseUser.organization, 
                    firebaseUser.uid, 
                    async (docs) => {
                        setDocuments(docs);
                        // Fetch URLs for documents
                        if (docs.length > 0) {
                            const urlPromises = docs.map(async (doc) => {
                                const result = await getEmployeeDocumentDownloadURL(
                                    firebaseUser.organization,
                                    firebaseUser.uid,
                                    doc.id
                                );
                                return { id: doc.id, url: result.success ? result.data : null };
                            });

                            const urls = await Promise.all(urlPromises);
                            const urlMap = {};
                            urls.forEach(({id, url}) => {
                                urlMap[id] = url;
                            });
                            setDocumentUrls(urlMap);
                        }
                    }
                );

                const unsubscribeReviews = employeePerformanceReviewsSnapshotListener(
                    firebaseUser.organization, 
                    firebaseUser.uid, 
                    setPerformanceReviews
                );

                setLoadingData(false);
                
                return () => {
                    unsubscribeDocuments();
                    unsubscribeReviews();
                };
            } catch (error) {
                console.error("Error loading profile data:", error);
                setLoadingData(false);
            }
        };

        loadAllData();
    }, [firebaseUser, setLoadingData]);

    return (
        <>
        { firebaseUser ? (
            <Stack gap={4}>
              <Card variant="outlined">
                <CardContent>
                <Typography variant={isMobile ? "h6" : "h5"} component="div">
                    {t(StringKeys.WORK_AND_CONTACT_INFORMATION)}
                  </Typography>
                  <Typography>
                    {t(StringKeys.PHONE)}: {firebaseUser.phone_number}
                  </Typography>
                  <Typography>
                    {t(StringKeys.DEPARTMENT)}: {' '}

                    {(() => {
                      const department = getDepartmentByID(firebaseUser.department_id);
                      return department ? department.name : t(StringKeys.UNASSIGNED);
                    })()}
                  </Typography>
                </CardContent>
              </Card>
              <Card variant="outlined">
                <CardContent>
                <Typography variant={isMobile ? "h6" : "h5"} component="div">
                    {t(StringKeys.ABOUT)}
                  </Typography>
                  <Typography>
                    {t(StringKeys.HIRE_DATE)}: {formatFirestoreTimestampMMDDYYYY(firebaseUser.hire_date)}
                  </Typography>
                  <Typography>{t(StringKeys.ROLE)}: {role}</Typography>
                  {/* <Typography>{t(StringKeys.RATE)}: ${firebaseUser.rate}</Typography> */}
                  </CardContent>
                </Card>
                {/* <Card variant="outlined">
                <CardContent>

                <Typography variant={isMobile ? "h6" : "h5"} component="div">
                  {t(StringKeys.X_DOCUMENTS, {x:`${firebaseUser.first_name}`})}
                </Typography>                
                  <TableContainer>
                    <Table aria-label="documents table">
                        <TableHead>

                        </TableHead>
                        <TableBody>
                          {
                            documents.length > 0 ? (
                              documents.map((doc) => (
                                <TableRow key={doc.id} sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                                  <TableCell component="th" scope="row">
                                    <Link 
                                        href={documentUrls[doc.id]} 
                                        target="_blank" 
                                        rel="noopener noreferrer"
                                    >
                                        {doc.title}
                                    </Link>
                                  </TableCell>
                                </TableRow>
                              ))
                            ) : (
                              <TableRow>
                                <TableCell colSpan={3} align="center">
                                  <Typography variant="body2">{t(StringKeys.NO_DOCUMENTS_TO_SHOW)}</Typography>
                                </TableCell>
                              </TableRow>
                            )
                          }
                        </TableBody>
                    </Table>
                </TableContainer>
                </CardContent>
                </Card>     */}
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant={isMobile ? "h6" : "h5"} component="div">
                      {t(StringKeys.PERFORMANCE_REVIEWS)}
                    </Typography>                
                    <TableContainer>
                      <Table aria-label="performance reviews table">
                        {performanceReviews?.length > 0 ? (
                          <>
                            <TableHead>
                              <TableRow>
                                {/* <TableCell>{t(StringKeys.TITLE)}</TableCell> */}
                                <TableCell>{t(StringKeys.DUE_DATE)}</TableCell>
                                <TableCell>{t(StringKeys.SELF_EVALUATION)}</TableCell>
                                <TableCell>{t(StringKeys.MANAGER_EVALUATION)}</TableCell>
                                <TableCell>{t(StringKeys.REVIEW_COMPLETED)}</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {performanceReviews.map((review) => (
                                <TableRow 
                                  key={review.id} 
                                  sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                                >
                                  {/* <TableCell sx={{ cursor: 'pointer' }}>
                                    <Link href={review.url}
                                        onClick={() => {
                                          navigate(`/performance-reviews/${review.id}`);
                                      }}
                                    target="_blank" rel="noopener noreferrer">
                                      {review.title}
                                    </Link>
                                  </TableCell> */}
                                  <TableCell>
                                    {formatFirestoreTimestampMMDDYYYY(review.due_date)}
                                  </TableCell>
                                  <TableCell>
                                    <Chip 
                                      label={t(review.status.employee)} 
                                      color={review.status.employee === 'completed' ? 'success' : 'warning'}
                                      size="small"
                                    />
                                  </TableCell>
                                  <TableCell>
                                    <Chip 
                                      label={t(review.status.manager)} 
                                      color={review.status.manager === 'completed' ? 'success' : 'warning'}
                                      size="small"
                                    />
                                  </TableCell>
                                  <TableCell>
                                    <Chip 
                                      label={review.acknowledgments?.employee_acknowledged && 
                                            review.acknowledgments?.manager_acknowledged ? 
                                            t(StringKeys.YES) : t(StringKeys.NO)}
                                      color={review.acknowledgments?.employee_acknowledged && 
                                            review.acknowledgments?.manager_acknowledged ? 
                                            'success' : 'warning'}
                                      size="small"
                                    />
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </>
                        ) : (
                          <TableBody>
                            <TableRow>
                              <TableCell colSpan={4} align="center">
                                <Typography variant="body2">
                                  {t(StringKeys.NO_PERFORMANCE_REVIEWS_TO_DISPLAY)}
                                </Typography>
                              </TableCell>
                            </TableRow>
                          </TableBody>
                        )}
                      </Table>
                    </TableContainer>
                  </CardContent>
                </Card>
            </Stack>
        ) : <Loading/>
    }
    </>
        
    );
}

export default Profile;